import React, { useEffect, useState, useRef } from 'react';
import styles from './style.module.scss';
import ReviewHeader from '../common/ReviewHeader';
import DigitalSignature from '../../../../../digital-signature';
import GeneratedSign from './component/generated-sign';
import useSalesFormStore from '../../../../../../../../stores/sales-form';
import ReactSignatureCanvas from 'react-signature-canvas';
import Button from '../../../../../../../../components/new-button';
import ConfirmPdfModal from './component/confirm-pdf-modal';
import { uploadSignature, reUploadPdf } from '../../../../../../../../../api';
import momentTimezone from 'moment-timezone';
import { getPdfUrl } from '../../../../../../../../../components/helper/helper';
import toast from 'react-hot-toast';
import useUserStore from '../../../../../../../../stores/user';

/**
 * SignAgreement Component
 * Renders the sign agreement section with signature options and documents
 * 
 * @param {Object} props - Component props
 * @param {Object} props.formik - Formik form instance containing form values
 * @param {Function} props.onEdit - Function to handle edit action
 */
const SignAgreement = ({ formik, onEdit, reRender }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('select-style');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [signedName, setSignedName] = useState("");
  const [signedInitials, setSignedInitials] = useState('');
  const [signedImages, setSignedImages] = useState({
    signedName: '',
    signedInitials: '',
  });
  const [isSigned, setIsSigned] = useState(false);

  const [signatureImage, setSignatureImage] = useState("");
  const [signedPdfList, setSignedPdfList] = useState([]);
  const [consentPdf, setConsentPdf] = useState(false);
  const [consentPdfStatus, setConsentPdfStatus] = useState({
    totalPdf: 0,
    viewedPdf: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [confirmSignedPdf, setConfirmSignedPdf] = useState(false);

  // store states
  const applicationData = useSalesFormStore(state => state.applicationData);
  const otpVerificationTime = useSalesFormStore(state => state.otpVerificationTime);
  const setOtpVerificationTime = useSalesFormStore(state => state.setOtpVerificationTime);
  const signerType = useSalesFormStore(state => state.signerType);
  const setSignerType = useSalesFormStore(state => state.setSignerType);
  const setIsSignInPersonModalOpen = useSalesFormStore(state => state.setIsSignInPersonModalOpen);
  const setStep = useSalesFormStore(state => state.setStep);
  // user store states
  const user = useUserStore(state => state.user);

  // ref for signature canvas
  const sigPad = useRef(null);

  useEffect(() => {
    setSignedName(`${firstName} ${lastName}`);
    setSignedInitials(`${firstName?.charAt(0)}${lastName?.charAt(0)}`);
  }, [firstName, lastName]);

  const onToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleSignedImages = async () => {
    try {
      // signedName, signedInitials
      console.log('signedName', signedName, signedName.length);
      if (signedName.length === 1) {
        console.log('signedName is empty');
        setIsSigned(false);
        setSignatureImage("");
        setSignedImages({
          signedName: "",
          signedInitials: "",
        });
        return;
      }
      console.log('signedInitials', signedInitials);
      // Define the font and other styles
      const fontSize = 70;
      const fontFamily = "Adobe Handwriting Ernie";
      const font = `${fontSize}px ${fontFamily}`;
      const text_color = "black";

      // Measure the text widths for signedName and signedInitials
      const tempCanvas = document.createElement("canvas");
      const tempContext = tempCanvas.getContext("2d");
      tempContext.font = font;
      const nameWidth = tempContext.measureText(signedName).width;
      const initialsWidth = tempContext.measureText(signedInitials).width;

      // Determine the maximum width for both canvases
      let maxWidth = Math.max(nameWidth, initialsWidth) + 25;
      let minWidth = 300;
      if (maxWidth < minWidth) {
        maxWidth = minWidth;
      }

      // Create a canvas for the name with the maximum width
      const nameCanvas = document.createElement("canvas");
      nameCanvas.width = maxWidth;
      const nameContext = nameCanvas.getContext("2d");

      // Create a canvas for the initials with the maximum width
      const initialsCanvas = document.createElement("canvas");
      initialsCanvas.width = maxWidth;
      const initialsContext = initialsCanvas.getContext("2d");

      // Calculate positions
      const hasNameSpecialCharacter = signedName.match(/[fgjpqyz]/i);
      const hasInitialSpecialCharacter = signedInitials.match(/[fgjpqyz]/i);
      const x = 0;
      const nameY = hasNameSpecialCharacter ? 100 : 120;
      const initialY = hasInitialSpecialCharacter ? 100 : 120;

      // Draw the name on the name canvas
      nameContext.font = font;
      nameContext.fillStyle = text_color;
      nameContext.fillText(signedName, x, nameY);

      // Draw the initials on the initials canvas
      initialsContext.font = font;
      initialsContext.fillStyle = text_color;
      initialsContext.fillText(signedInitials, x, initialY);

      setSignedImages({
        signedName: nameCanvas ? nameCanvas.toDataURL("image/png") : "",
        signedInitials: initialsCanvas
          ? initialsCanvas.toDataURL("image/png")
          : "",
      });
      setSignatureImage(nameCanvas ? nameCanvas.toDataURL("image/png") : "");
      setIsSigned(true);
    } catch (err) {
      console.log('error in handleSignedImages', err);
      setIsSigned(false);
    }
  };

  const createSiganture = () => {
    const signature = Object.keys(sigPad.current).length !== 0 && !sigPad.current.isEmpty()
      ? sigPad.current.getTrimmedCanvas().toDataURL("image/png")
      : "";
    setSignatureImage(signature);
  }

  const getSignSlug = async () => {
    let signaturesList = [];
    let signSlug;
    try {

      if (Array.isArray(applicationData?.owners)) {
        signaturesList = applicationData?.owners?.filter(
          (item) => item?.active === 1
        );
      }

      if (signerType) {
        if (signerType === "First") {
          const firstSigner = signaturesList?.filter(
            (item) => item?.signer_id === null
          )?.[0];
          signSlug = firstSigner?.slug;
        } else if (signerType === "Second") {
          const secondSigner = signaturesList?.filter(
            (item) => item?.signer_id !== null
          )?.[0];
          signSlug = secondSigner?.slug;
        }
      } else {
        signSlug = signaturesList?.[0]?.slug;
      }

    } catch (error) {
      signSlug = undefined;
    }
    return signSlug;
  }

  const reUploadSignedPdf = async (editSlug, pdfList) => {
    // const signerType = this.props.signerType;
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezoneAbbreviation = momentTimezone
      .tz(momentTimezone.tz.guess())
      .format("z");
    const uploadPromises = pdfList?.map(async (pdf) => {
      return new Promise((resolve, reject) => {
        const pdfURL = getPdfUrl(pdf?.signedPdf.url, signerType);
        const form = new FormData();
        form.append("pdf", `${pdfURL}`);
        form.append("editSlug", editSlug);
        form.append("pdfName", pdf?.signedPdf?.name);
        form.append("signerType", signerType);
        form.append("mainURL", pdf?.signedPdf.url);
        form.append("userTimezone", userTimezone);
        form.append("timezoneAbbreviation", timezoneAbbreviation);
        return reUploadPdf(editSlug, form)
          .then((response) => {
            resolve(response?.data?.status);
          })
          .catch((err) => {
            reject(err);
          });
      });
    });
    try {
      const results = await Promise.all(uploadPromises);
      return results;
    } catch (err) {
      throw err;
    }
  };

  const submitApplication = async () => {
    setIsLoading(true);
    let data = {
      sigslug: await getSignSlug(),
      id: applicationData?.id,
      otp_verified_at: otpVerificationTime,
    };

    uploadSignature(applicationData?.editslug, data)
      .then(async (response) => {
        if (response.status == true) {
          const signedPDF = signedPdfList;
          const editSlug = applicationData?.editslug;
          await reUploadSignedPdf(editSlug, signedPDF);
          setIsLoading(false);
          setFirstName('');
          setLastName('');
          setSignedName('');
          setSignedInitials('');
          setIsSigned(false);
          setAgreeToTerms(false);
          setSignatureImage("");
          setSignedImages({
            signedName: "",
            signedInitials: "",
          });
          if (user) {
            reRender();
            setIsSignInPersonModalOpen(true);
          } else {
            setStep(8);
          }
          setOtpVerificationTime(null);
          toast.success('Application submitted successfully');
        }
      })
      .catch(error => {
        console.log('error', error);
        setIsLoading(false);
        toast.error('Error submitting application');
      });
  }


  const handleApplicationSubmit = async () => {
    if (consentPdfStatus?.totalPdf === consentPdfStatus.viewedPdf) {
      submitApplication();
    } else {
      setConfirmSignedPdf(true);
    }
  }

  return (
    <div className={styles.signAgreementSection}>
      <ReviewHeader
        title="Sign Agreement"
        isOpen={isOpen}
        onToggle={onToggle}
        onEdit={onEdit}
      />

      {isOpen && (
        <div className={styles.content}>

          {activeTab === 'select-style' && (
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>First Name</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  className={styles.formInput}
                  placeholder="First Name"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    setIsSigned(false);
                    setSignatureImage("");
                    setSignedImages({
                      signedName: "",
                      signedInitials: "",
                    });
                  }}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  className={styles.formInput}
                  placeholder="Last Name"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    setIsSigned(false);
                    setSignatureImage("");
                    setSignedImages({
                      signedName: "",
                      signedInitials: "",
                    });
                  }}
                />
              </div>
              <button
                className={styles.adoptButton}
                onClick={handleSignedImages}
                type="button"
              >
                Adopt Signature
              </button>
            </div>
          )}

          <div className={styles.signatureContainer}>
            <div className={styles.tabGroup}>
              <button
                className={`${styles.tabItem} ${activeTab === 'select-style' ? styles.active : ''}`}
                onClick={() => setActiveTab('select-style')}
              >
                Select Style
              </button>
              <button
                className={`${styles.tabItem} ${activeTab === 'draw-signature' ? styles.active : ''}`}
                onClick={() => setActiveTab('draw-signature')}
              >
                Draw Signature
              </button>
            </div>

            {activeTab === 'select-style' && (
              <GeneratedSign signedImages={signedImages} />
            )}

            {activeTab === 'draw-signature' && (
              <div className={styles.drawSignatureContainer} >
                <ReactSignatureCanvas
                  penColor="black"
                  canvasProps={{
                    width: 1100,
                    height: 150,
                    className: styles.signatureCanvas,
                  }}
                  ref={sigPad}
                  onEnd={() => {
                    setIsSigned(true)
                    createSiganture()
                  }}
                />
              </div>
            )}

            <div className={styles.checkboxWrapper}>
              <label className={styles.checkboxContainer}>
                <input
                  type="checkbox"
                  checked={agreeToTerms}
                  onChange={() => setAgreeToTerms(!agreeToTerms)}
                />
                <span className={styles.checkmark}></span>
                <span className={styles.checkboxText}>I agree to use electronic records and signatures</span>
                <span className={styles.checkboxText2}>I agree to use electronic records and signatures</span>
              </label>
            </div>

            <p className={styles.legalText}>
              By Clicking the "Apply Signature & Submit" button, you acknowledge that (a) you ore submitting an application to receive merchat processing services from each of the merchant service providers that vou select below. and (b)you have received, read, and agree to be bound by the merchant services terms and conditions for each such merchant service provider hyperlinked below. You acknowledge and agree your electronic signature to these terms and conditions has the same legal effect and validity as your written signature would have. You also represent that you are duly authorised to bind your organization to such terms and conditions.
            </p>

            <div className={styles.documentsContainer}>
              <DigitalSignature
                sign={signatureImage}
                signedName={signedName}
                setSignedPdfList={(data) => { setSignedPdfList(data) }}
                signedPdfList={signedPdfList}
                progressId={applicationData?.progression_id}
                viewOnly={false}
                signature={applicationData?.owners}
                bankList={applicationData?.override_bank}
                editslug={applicationData?.editslug}
                combinedFiles={applicationData?.combined_files}
                isDisable={(agreeToTerms && signatureImage.length > 0) ? false : true}
                handleChangeConsentPdf={(data) => { setConsentPdf(data) }}
                handleConsentPdfStatus={(data) => { setConsentPdfStatus(data) }}
                signerType={signerType}
              />

            </div>
          </div>

          <Button
            variant="primary"
            fullWidth={true}
            onClick={handleApplicationSubmit}
            disabled={
              signedPdfList?.length < 1 ||
              consentPdfStatus.viewedPdf < 1 ||
              !agreeToTerms ||
              signatureImage.length === 0
            }
            isLoading={isLoading}
          >
            Apply Signature & Submit
          </Button>

        </div>
      )}

      {/* Confirm PDF Modal */}
      <ConfirmPdfModal
        isOpen={confirmSignedPdf}
        onClose={() => setConfirmSignedPdf(false)}
        consentPdfStatus={consentPdfStatus}
        submitApplication={submitApplication}
      />
    </div>
  );
};

export default SignAgreement; 